import { MapPoint } from '@/domain/kart/MapPoint';
import { Egenskapstype } from '@/domain/vegobjekter/Egenskapstype';
import { Operator } from '@/middleware/queries';
import { Stedsnavn } from '@/server/responses';
import { advancedFilterToHash } from '@/domain/advancedFilter';
import {
    stateMap,
    stateOmrader,
    statePlacenameMarker,
    stateSearchDropdownState,
    stateSplash,
    stateValgt,
    stateVegnett,
    stateVegobjekter,
    stateVegsystemreferanseSok,
} from '@/selectors/selectors';
import {
    MapState,
    OmraderState,
    RootState,
    ValgtState,
    VegnettState,
    VegobjekterState,
    VegsystemreferanseLookup,
} from '@/state';
import { format } from 'date-fns';
import qs from 'qs';
import { SplashState } from '../SplashState';

interface CategoryRepresentation {
    type: 'interval' | 'enum';
    id: number;
    intervals?: number[];
}

interface SelectedVegobjektHash {
    id: number;
    type: number;
}

interface StructuredValgtHash {
    vegobjekter?: SelectedVegobjektHash[];
    clusterObjekter?: SelectedVegobjektHash[];
}

interface PlacenameMarkerHash {
    ssrID?: number;
    placeNameType?: string;
    category?: string;
    name: string;
    kommune?: string;
    fylke?: string;
    xCoord: string;
    yCoord: string;
}

export function stateToHash(state: RootState): string {
    return [
        mapStateToHash(stateMap(state)),
        hvaToHash(stateVegobjekter(state)),
        hvorToHash(stateOmrader(state)),
        roadNetToHash(stateVegnett(state)),
        selection(stateValgt(state)),
        vegsystemreferanseToHash(stateVegsystemreferanseSok(state)),
        placenameMarkerToHash(statePlacenameMarker(state)),
        splashToHash(stateSplash(state)),
        timestampToHash(stateSearchDropdownState(state).timestamp),
        markoreToHash(stateMap(state).points),
    ]
        .filter(Boolean)
        .join('');
}

function markoreToHash(points: MapPoint[]): string {
    if (points.length === 0) return '';
    return `/markorer:${points.map(point => point.geometry.join(',')).join(';')}`;
}

function placenameMarkerToHash(placenameMarker: Stedsnavn | null): string {
    if (!placenameMarker) return '';

    return `/stedsnavn:${qs.stringify(
        removeEmptyProperties<PlacenameMarkerHash>({
            ssrID: placenameMarker.ssrID,
            placeNameType: placenameMarker.placeNameType || undefined,
            category: placenameMarker.category || undefined,
            name: placenameMarker.name,
            kommune: placenameMarker.kommune || undefined,
            fylke: placenameMarker.fylke || undefined,
            xCoord: placenameMarker.xCoord,
            yCoord: placenameMarker.yCoord,
        })
    )}`;
}

function timestampToHash(timestamp: Date | null): string {
    return timestamp ? `/når:${format(timestamp, 'yyyy-MM-dd')}` : '';
}

function hvaToHash(hva: VegobjekterState): string {
    if (hva.vegobjekttypeStates.length === 0) return '';

    const hvaObject = {
        hva: hva.vegobjekttypeStates.map(type =>
            (() => {
                const advancedFilter = advancedFilterToHash(
                    type.getAdvancedFilterApplied()
                );
                return removeEmptyProperties({
                    category: stringifyCategory(type.category),
                    intervals: type.intervals,
                    absoluteIntervals: type.absoluteIntervals,
                    advanced_filter: advancedFilter,
                    filter: advancedFilter
                        ? undefined
                        : type.filters
                              .filter(f => f.isValid())
                              .map(t => ({
                                  operator: Operator.asString(t.operator),
                                  type_id: t.egenskapstype.id,
                                  verdi:
                                      t.verdiValue() === null ? null : [t.verdiValue()],
                              })),
                    id: type.typeId,
                });
            })()
        ),
    };

    return `/hva:${qs.stringify(hvaObject, { arrayFormat: 'indices' })}`;
}

function hvorToHash(hvor: OmraderState): string {
    const {
        kommune,
        fylke,
        riksvegrute,
        kontraktsomrade,
        land,
        vegsystemreferanse,
        polygon,
    } = hvor;

    if (
        [kommune, fylke, riksvegrute, kontraktsomrade, land, vegsystemreferanse].every(
            arr => arr.length === 0
        ) &&
        !polygon
    ) {
        return '';
    }

    return `/hvor:${qs.stringify(removeEmptyProperties(mapHvor(hvor)))}`;
}

function mapStateToHash(mapState: MapState): string {
    return `/@${mapState.center[0]},${mapState.center[1]},${mapState.zoom}`;
}

function selection(selected: ValgtState): string {
    let hash = '';

    if (Object.keys(selected.linkQueries).length > 0) {
        hash += `/lenker:${qs.stringify({ links: Object.keys(selected.linkQueries) })}`;
    }

    if (shouldUseStructuredValgtHash(selected)) {
        hash += `/valgt:${qs.stringify(
            removeEmptyProperties<StructuredValgtHash>({
                vegobjekter: serializeSelectedVegobjekter(selected.vegobjekter),
                clusterObjekter: serializeSelectedVegobjekter(selected.clusterObjekter),
            })
        )}`;
    } else if (selected.isSingle()) {
        if (selected.singleVegobjekt()) {
            hash += `/valgt:${generateSelectedObjectHash(selected)}`;
        } else if (
            selected.singleVegnettlenke() ||
            selected.singleVegnettlenkeReferanse()
        ) {
            hash += `/veglenke:${generateSelectedObjectHash(selected)}`;
        }
    }

    if (selected.nodes.length > 0) {
        hash += `/noder:${qs.stringify({ nodes: selected.nodes.map(n => n.id) })}`;
    }

    return hash;
}

function shouldUseStructuredValgtHash(selected: ValgtState): boolean {
    return selected.vegobjekter.length > 1 || selected.clusterObjekter.length > 0;
}

function serializeSelectedVegobjekter(
    vegobjekter: ValgtState['vegobjekter']
): SelectedVegobjektHash[] {
    return vegobjekter.map(vegobjekt => ({
        id: vegobjekt.id,
        type: vegobjekt.metadata.type.id,
    }));
}

function generateSelectedObjectHash(selected: ValgtState): string {
    const vo = selected.singleVegobjekt();
    const vnl = selected.singleVegnettlenke();
    const vnlReferanse = selected.singleVegnettlenkeReferanse();

    if (vo) {
        return `${vo.id}:${vo.metadata.type.id}`;
    }
    if (vnl) {
        return `${vnl.veglenkesekvensid}:${vnl.veglenkenummer}:${vnl.segmentnummer}`;
    }
    if (vnlReferanse) {
        const segment =
            vnlReferanse.segmentnummer == null ? '' : `:${vnlReferanse.segmentnummer}`;
        return `${vnlReferanse.veglenkesekvensid}:${vnlReferanse.veglenkenummer}${segment}`;
    }
    return '';
}

function vegsystemreferanseToHash(
    vegsystemreferansesok: VegsystemreferanseLookup | null
): string {
    if (!vegsystemreferansesok) return '';

    const vegsystemreferanse = vegsystemreferansesok.vegsystemreferanse;

    if (vegsystemreferanse?.senterpunkt || vegsystemreferanse?.geometri) {
        return `/vegsystemreferanse:${vegsystemreferanse.toParams()}`;
    }

    if (vegsystemreferansesok.east && vegsystemreferansesok.north) {
        return `/vegsystemreferanse:${vegsystemreferansesok.east}:${vegsystemreferansesok.north}`;
    }

    return '';
}

function splashToHash(splash: SplashState): string {
    return splash ? `/splash:${SplashState[splash].toLowerCase()}` : '';
}

function roadNetToHash(roadNetState: VegnettState): string {
    return !roadNetState.visible
        ? ''
        : `/vegnett:${qs.stringify({
              visible: roadNetState.visible,
              direction: roadNetState.direction,
              filter: roadNetState.filter,
          })}`;
}

function mapHvor(omrade: OmraderState) {
    return {
        fylke: omrade.fylke.map(f => f.nummer),
        kommune: omrade.kommune.map(k => k.nummer),
        kontraktsomrade: omrade.kontraktsomrade.map(k => k.navn),
        land: omrade.land.map(l => l.navn),
        riksvegrute: omrade.riksvegrute.map(r => `${r.enumid}`),
        vegsystemreferanse: omrade.vegsystemreferanse.map(v => v?.toParams()),
        polygon: omrade.polygon?.wkt.wkt,
        skjult: hiddenAreasToHash(omrade),
    };
}

function hiddenAreasToHash(omrade: OmraderState) {
    const hidden = <T extends { visible: boolean }, K>(areas: T[], id: (area: T) => K) =>
        areas.filter(area => !area.visible).map(id);
    const skjult = removeEmptyProperties({
        fylke: hidden(omrade.fylke, f => f.nummer),
        kommune: hidden(omrade.kommune, k => k.nummer),
        kontraktsomrade: hidden(omrade.kontraktsomrade, k => k.navn),
        land: hidden(omrade.land, l => l.navn),
        riksvegrute: hidden(omrade.riksvegrute, r => `${r.enumid}`),
        vegsystemreferanse: hidden(omrade.vegsystemreferanse, v => v.toParams()),
        polygon: omrade.polygon?.visible === false ? 'true' : undefined,
    });
    return Object.keys(skjult).length > 0 ? skjult : undefined;
}

function stringifyCategory(
    category: Egenskapstype | null
): CategoryRepresentation | null {
    if (!category || !category.egenskapstype) return null;

    if (category.erEnum()) {
        return { type: 'enum', id: category.id };
    }

    if (category.erNummerisk()) {
        return { type: 'interval', id: category.id };
    }

    return null;
}

function removeEmptyProperties<T extends object>(original: T): T {
    return Object.entries(original)
        .filter(
            ([_, value]) =>
                !(value == null || (Array.isArray(value) && value.length === 0))
        )
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {}) as T;
}
